"use client";

import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionStyle,
} from "framer-motion";

interface IParallaxLayerProps {
  children: React.ReactNode;
  /**
   * Parallax speed, matching the design handoff's `data-px` values.
   * Positive values drift with the scroll direction, negative against it.
   * Typical range: -0.16 … 0.16.
   */
  speed: number;
  className?: string;
  /** Extra inline styles (e.g. gradient backgrounds for orbs). */
  style?: React.CSSProperties;
  /** Marks purely decorative layers for assistive tech. */
  ariaHidden?: boolean;
}

/**
 * Scroll-linked parallax wrapper. Elements drift vertically relative to their
 * distance from the viewport centre, mirroring the design's `data-px` engine.
 * Renders static (no drift) when the user prefers reduced motion.
 */
export function ParallaxLayer({
  children,
  speed,
  className,
  style,
  ariaHidden,
}: IParallaxLayerProps): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // progress 0 → element entering from below (positive offset for +speed),
  // progress 1 → element leaving above. 500px approximates half the travel.
  // Function form keeps this on framer's JS path — keyframe form compiles to
  // WAAPI scroll timelines that don't track element-targeted progress.
  const y = useTransform(scrollYProgress, (p) => speed * 500 * (1 - 2 * p));

  // Cast: spreading optional CSSProperties into MotionStyle trips
  // exactOptionalPropertyTypes even though undefined values are fine here.
  const combinedStyle = {
    ...style,
    y: prefersReducedMotion ? 0 : y,
  } as MotionStyle;

  return (
    <motion.div
      ref={ref}
      className={className}
      style={combinedStyle}
      {...(ariaHidden != null && { "aria-hidden": ariaHidden })}
    >
      {children}
    </motion.div>
  );
}
